import { $, setLoading } from './dom.js';
import { ct } from './i18n.js';
import { toastError, toastSuccess, confirmAction } from './toast.js';
import { ApiError } from './api.js';
import { warnAfterSave } from './health.js';
import { revealSection } from './section-nav.js';

/*
 * Tracking of a form's changes and floating save bar.
 *
 * This logic was copied into every file of the old dashboard
 * (`m-savechanges.js`, `m-savepopup.js`, plus inline variants in four views),
 * each copy having drifted apart. Here it is written once.
 */

/**
 * Form the navigation guards consult, if any.
 *
 * The guards live on `document` and `window`, so they are installed **once**
 * rather than on every call. Registering them per form stacked one listener per
 * call, each closing over its own `dirty`: a page that mounted the tracker
 * twice asked twice for confirmation, and a listener from a previous form went
 * on vetoing navigation for a form that no longer existed.
 */
let guarded: { isDirty: () => boolean; release: () => void } | null = null;

/**
 * Warns before leaving with unsaved changes.
 *
 * Two mechanisms, for a precise reason: the text of the `beforeunload` box is
 * **imposed by the browser** and follows its language, not the site's — no API
 * allows changing it, it is a protection against pages that used to trap the
 * user. We therefore reserve it for the one case where it is unavoidable
 * (closing a tab, reloading) and intercept internal navigation ourselves, with
 * our own translated modal.
 */
function guardNavigation(): void {
  document.addEventListener('click', (event) => {
    if (!guarded?.isDirty()) return;

    const link = (event.target as HTMLElement | null)?.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    // External links, new windows and anchors: none of our business.
    if (link.target === '_blank' || link.origin !== window.location.origin) return;
    if (link.getAttribute('href')?.startsWith('#')) return;

    event.preventDefault();
    const destination = link.href;

    void (async () => {
      if (await confirmAction(ct('leaveUnsaved', 'You have unsaved changes. Leave anyway?'))) {
        guarded?.release();
        window.location.href = destination;
      }
    })();
  });

  window.addEventListener('beforeunload', (event) => {
    if (guarded?.isDirty()) event.preventDefault();
  });
}

guardNavigation();

export interface DirtyFormOptions {
  form: HTMLFormElement;
  /** Performs the save; any error thrown is displayed to the user. */
  onSave: () => Promise<void>;
  successMessage?: string;
}

export function trackDirtyForm({
  form,
  onSave,
  successMessage = ct('saved', 'Changes saved.'),
}: DirtyFormOptions): {
  markClean: () => void;
  markDirty: () => void;
  /** `true` while the form holds changes that were never sent. */
  isDirty: () => boolean;
} {
  const bar = $('[data-save-bar]', form) ?? $('[data-save-bar]');
  const submitButton = bar?.querySelector<HTMLButtonElement>('[data-save-submit]') ?? null;
  const resetButton = bar?.querySelector<HTMLButtonElement>('[data-save-reset]') ?? null;

  let dirty = false;

  const render = () => {
    if (!bar) return;
    bar.classList.toggle('opacity-0', !dirty);
    bar.classList.toggle('translate-y-4', !dirty);
    bar.classList.toggle('pointer-events-none', !dirty);
  };

  const markDirty = () => {
    dirty = true;
    render();
  };

  const markClean = () => {
    dirty = false;
    render();
  };

  form.addEventListener('input', markDirty);
  form.addEventListener('change', markDirty);

  /**
   * Cancelling discards the changes by reloading the saved state.
   *
   * It used to restore from a `FormData` snapshot, which could only ever be
   * half right, so the button closed the bar while leaving most of the page
   * modified:
   *
   *   • `FormData` carries only the **checked** boxes, so no snapshot can
   *     express "this one was unchecked" - checkboxes were therefore skipped
   *     outright, in both directions;
   *   • the select menus, colour pickers and role pickers render from their own
   *     internal state, which writing to the backing field does not drive. The
   *     value changed underneath, the visible chip did not.
   *
   * A partial restore is worse than none: the user believes everything is back.
   * Reloading is correct by construction for every widget, including the ones
   * added later, and "cancel" means precisely "show me what is saved" - which
   * is what the server renders.
   *
   * `dirty` is cleared first, otherwise our own guards below would intercept
   * the reload and ask the user to confirm leaving.
   */
  resetButton?.addEventListener('click', () => {
    dirty = false;
    window.location.reload();
  });

  /*
   * One save at a time. The bar's button is disabled while it works, but the
   * form's own `submit` event is a second trigger — Enter in a lone text
   * field — and nothing gated it: on a creation page, two resources.
   */
  let saving = false;

  const save = async (event?: Event) => {
    event?.preventDefault();
    if (saving || !form.reportValidity()) return;

    saving = true;
    setLoading(submitButton, true);
    try {
      await onSave();
      markClean();
      toastSuccess(successMessage);

      /*
       * "Saved" answers a question nobody asked. What an administrator wants to
       * know is whether it works now — and pointing a module at a channel the
       * bot cannot write in saves perfectly and does nothing at all.
       *
       * Not awaited: the save is finished, and an audit that hangs must not
       * hold the button in its loading state.
       */
      void warnAfterSave();
    } catch (error) {
      /*
       * Point at the field, not only at the toast.
       *
       * A refused save says which field it was — the API has carried that
       * detail since it was written — and nothing read it: a server was
       * refused four times in forty seconds on the same page, retrying
       * blindly. The toast still says what is wrong; the page now says where.
       */
      pointAtField(form, error);
      toastError(error);
    } finally {
      saving = false;
      setLoading(submitButton, false);
    }
  };

  submitButton?.addEventListener('click', (event) => void save(event));
  form.addEventListener('submit', (event) => void save(event));

  // The navigation guards are global and installed once; this form becomes the
  // one they consult. See `guardNavigation` below.
  guarded = {
    isDirty: () => dirty,
    release: () => {
      dirty = false;
    },
  };

  render();
  return { markClean, markDirty, isDirty: () => dirty };
}

/**
 * The field a refusal names, marked and scrolled to.
 *
 * The API answers a 400 with `details: [{ field, message }]`, straight from the
 * schema that refused it. The name is the one the payload uses, which is the
 * one the control carries — as `name`, as `data-field`, or on the host of an
 * upgraded select. A path like `rules.0.roleId` is tried whole first, then by
 * its last segment, which is the only part a form control is ever named after.
 */
function pointAtField(form: HTMLFormElement, error: unknown): void {
  const details = error instanceof ApiError ? error.details : null;
  if (!Array.isArray(details)) return;

  /*
   * Every field at fault, not the first one only, each scoped to its rule.
   *
   * The AutoMod tab holds five rules with the same field names: the first
   * `[data-field="timeoutSeconds"]` in the form belonged to the keyword rule
   * whatever rule was wrong, and the tab itself was hidden. A detail that
   * carries its rule's `kind` is looked for inside that rule, the tab holding
   * it is opened, and the sentence is written under the field.
   */
  let first: HTMLElement | null = null;

  for (const detail of details.slice(0, 10) as { field?: unknown; kind?: unknown; message?: unknown }[]) {
    const field = typeof detail.field === 'string' ? detail.field : '';
    if (!field) continue;

    const scope =
      (typeof detail.kind === 'string'
        ? form.querySelector<HTMLElement>(`[data-native-rule="${CSS.escape(detail.kind)}"]`)
        : null) ?? form;

    const target = findField(scope, field);
    if (!target) continue;

    mark(target, typeof detail.message === 'string' ? detail.message : '');
    first ??= target;
  }

  if (!first) return;
  revealSection(first);
  first.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/** The visible control for a field path: the upgraded select before the hidden one. */
function findField(scope: HTMLElement | HTMLFormElement, field: string): HTMLElement | null {
  const names = [field, field.split('.').pop() ?? ''].filter(Boolean);

  for (const name of names) {
    const escaped = CSS.escape(name);
    const target =
      scope.querySelector<HTMLElement>(
        `[data-select-host="${escaped}"], [data-select-host][data-field="${escaped}"]`,
      ) ??
      scope.querySelector<HTMLElement>(`[name="${escaped}"], [data-field="${escaped}"]`);
    if (target) return target;
  }

  return null;
}

/** Marks one field, and unmarks it the moment it is touched again. */
function mark(target: HTMLElement, message: string): void {
  target.classList.add('input-error');
  target.setAttribute('aria-invalid', 'true');

  const note = message ? document.createElement('p') : null;
  if (note) {
    note.className = 'field-hint text-error';
    note.dataset.fieldError = '';
    note.textContent = message;
    target.insertAdjacentElement('afterend', note);
  }

  // The mark is a hint, not a state: it goes as soon as the field is touched
  // again, whether or not what was typed is any better.
  const clear = () => {
    target.classList.remove('input-error');
    target.removeAttribute('aria-invalid');
    note?.remove();
  };
  target.addEventListener('input', clear, { once: true });
  target.addEventListener('change', clear, { once: true });
}
